/**
 * Match Score Breakdown Component
 * Displays detailed breakdown of how a job matches the user's profile
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { MatchScore } from '../../../../../utils/data/job-matching';

export interface MatchScoreBreakdownProps {
  matchScore: MatchScore;
}

export const MatchScoreBreakdown: React.FC<MatchScoreBreakdownProps> = ({ matchScore }) => {
  return (
    <div className="mt-6 p-6 bg-gray-50/80 dark:bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        <FormattedMessage
          id="jobDetailPage.matchBreakdown.title"
          defaultMessage="How this job matches your profile"
          description="Section heading for job match score breakdown showing how well the job matches user profile"
        />
      </h3>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.titleMatch" defaultMessage="Title" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.title}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.roleLevel" defaultMessage="Role Level" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.roleLevel}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.location" defaultMessage="Location" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.location}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.remoteness" defaultMessage="Remoteness" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.remoteness}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.jobType" defaultMessage="Job Type" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.jobType}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.salary" defaultMessage="Salary" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.salary}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.vacation" defaultMessage="Vacation" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.vacation}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.matchBreakdown.benefits" defaultMessage="Benefits" />
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{matchScore.breakdown.benefits}%</span>
        </div>
      </div>
    </div>
  );
};
